import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { RegisterModal } from '../components/RegisterModal';
import { CandidateCard } from '../components/CandidateCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Briefcase, Users, CheckCircle, TrendingUp, Search, ChevronRight, Building2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const HFS_LOGO = "https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/qxumdsjc_HFS_logo_192x192.png";

// Company logos for trusted by section - using actual uploaded images
const TRUSTED_COMPANIES = [
  // Row 1 (10 logos)
  { name: 'Shiprocket', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/74dawhao_WhatsApp%20Image%202026-01-17%20at%204.11.57%20PM.jpeg' },
  { name: 'LambdaTest', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/j9odaz1l_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%285%29.jpeg' },
  { name: 'Innovaccer', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/m7tbvxqo_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%288%29.jpeg' },
  { name: 'SurveySparrow', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/pibpnke4_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%282%29.jpeg' },
  { name: 'Flam', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/3ciiruit_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%283%29.jpeg' },
  { name: 'Chargebee', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/zscq7j2w_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2825%29.jpeg' },
  { name: 'Razorpay', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/ta3ng8yi_WhatsApp%20Image%202026-01-17%20at%204.16.35%20PM.jpeg' },
  { name: 'Adobe', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/2kgft3mf_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2816%29.jpeg' },
  { name: 'Whatfix', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/nj9ggz14_WhatsApp%20Image%202026-01-17%20at%204.14.45%20PM.jpeg' },
  { name: 'ClearTax', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/rs84kfim_WhatsApp%20Image%202026-01-17%20at%204.15.46%20PM.jpeg' },
  // Row 2 (9 logos)
  { name: 'Keka', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/ey7ya6wo_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2822%29.jpeg' },
  { name: 'WizCommerce', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/y13xakn7_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM.jpeg' },
  { name: 'Exotel', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/k7oe5uuc_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%289%29.jpeg' },
  { name: 'VWO', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/9nmyb6lv_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2811%29.jpeg' },
  { name: 'Nurix', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/s2906uwh_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2826%29.jpeg' },
  { name: 'SuperAGI', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/3uwodokw_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2824%29.jpeg' },
  { name: 'Beacon.li', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/9hukd79d_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%287%29.jpeg' },
  { name: 'Wayground', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/tk7h01h5_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2814%29.jpeg' },
  { name: 'Kaseya', logo: 'https://customer-assets.emergentagent.com/job_skill-profiles/artifacts/grwzxoct_WhatsApp%20Image%202026-01-17%20at%204.11.58%20PM%20%2817%29.jpeg' },
];

// Split into two rows for marquee
const LOGOS_ROW_1 = TRUSTED_COMPANIES.slice(0, 10);
const LOGOS_ROW_2 = TRUSTED_COMPANIES.slice(10);

export default function HomePage() {
  const { isAuthenticated, token } = useAuth();
  const [registerOpen, setRegisterOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [stats, setStats] = useState({ total_candidates: 0, placed_candidates: 0, active_candidates: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCandidates();
    fetchStats();
  }, [isAuthenticated, token]);

  const fetchCandidates = async () => {
    try {
      const endpoint = isAuthenticated ? `${API}/candidates/full` : `${API}/candidates`;
      const headers = isAuthenticated ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(endpoint, { headers });
      setCandidates(response.data);
    } catch (error) {
      console.error('Error fetching candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewPortfolio = (candidate) => {
    if (candidate.portfolio_link) {
      window.open(candidate.portfolio_link, '_blank');
    } else {
      toast.error('Portfolio link not available');
    }
  };

  // Filter by search and role
  const [roleFilter, setRoleFilter] = useState('all');
  const [visibleCount, setVisibleCount] = useState(6);

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      candidate.strength.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (roleFilter === 'all') return matchesSearch;
    
    const strengthLower = candidate.strength.toLowerCase();
    if (roleFilter === 'sdr') return matchesSearch && (strengthLower.includes('sdr') || strengthLower.includes('bdr'));
    if (roleFilter === 'ae') return matchesSearch && (strengthLower.includes('ae') || strengthLower.includes('account executive') || strengthLower.includes('closer'));
    if (roleFilter === 'enterprise') return matchesSearch && strengthLower.includes('enterprise');
    
    return matchesSearch;
  });

  // Mix placed and available candidates together (don't push placed to bottom)
  // Use a seeded shuffle based on candidate id to keep order consistent across refreshes
  const mixedCandidates = [...filteredCandidates].sort((a, b) => {
    // Create a simple hash from candidate ids for consistent ordering
    const hashA = a.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hashB = b.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return hashA - hashB;
  });

  const displayedCandidates = mixedCandidates.slice(0, visibleCount);
  const hasMore = mixedCandidates.length > visibleCount;

  const loadMore = () => {
    setVisibleCount(prev => prev + 6);
  };

  const showAll = () => {
    setVisibleCount(mixedCandidates.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onRegisterClick={() => setRegisterOpen(true)} />
      <RegisterModal open={registerOpen} onOpenChange={setRegisterOpen} />

      {/* Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                <CheckCircle className="h-4 w-4" />
                96% Placement Rate • 350+ Trained Professionals
              </div>
              
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Hire Interview-Ready <br />
                <span className="text-hfs-navy">SDRs & AEs</span> Trained on Real SaaS Playbooks
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-lg">
                <span className="font-semibold text-slate-800">Cut hiring time from 45 days to 7 days.</span> No recruiters. No guesswork. Our candidates are pre-trained on MEDDIC, BANT, and real enterprise sales motions.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#candidates">
                  <Button 
                    className="bg-hfs-navy hover:bg-hfs-navy-dark text-white h-12 px-8 text-base"
                    data-testid="browse-candidates-btn"
                  >
                    See Candidates Who Can Close
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </a>
                <a href="#testimonials">
                  <Button 
                    variant="outline" 
                    className="border-2 border-hfs-navy text-hfs-navy hover:bg-hfs-navy/5 h-12 px-8 text-base"
                    data-testid="learn-more-btn"
                  >
                    See Success Stories
                  </Button>
                </a>
              </div>
              
              {/* Quick trust badges */}
              <div className="flex items-center gap-6 pt-2 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  No hiring fees
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Interview in 48hrs
                </span>
              </div>
            </div>
            
            <div className="relative hidden md:block">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=600&h=500&fit=crop"
                  alt="Sales professionals in meeting"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-hfs-navy/20 to-transparent" />
              </div>
              
              {/* Stats Card Overlay */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-5 border border-slate-100">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-full">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">7 Days</p>
                    <p className="text-sm text-slate-500">Avg. Time to Hire</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FREE Hiring Banner */}
      <section className="py-6 bg-gradient-to-r from-green-500 to-emerald-600">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-center md:text-left">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-full">
                <DollarSign className="h-8 w-8 text-white" />
              </div>
              <div>
                <p className="text-2xl md:text-3xl font-bold text-white">100% FREE Hiring</p>
                <p className="text-green-100">Zero recruitment fees, forever</p>
              </div>
            </div>
            <div className="hidden md:block h-12 w-px bg-white/30" />
            <div className="text-white">
              <p className="text-lg">
                <span className="line-through opacity-70">8.33% recruiter fee</span>
                <span className="ml-3 bg-white/20 px-3 py-1 rounded-full font-semibold">Save ₹1-2L per hire</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-hfs-navy">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white">350+</p>
              <p className="text-slate-300 mt-1">Trained Professionals</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white">96%</p>
              <p className="text-slate-300 mt-1">Placement Rate</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white">28</p>
              <p className="text-slate-300 mt-1">Months of Excellence</p>
            </div>
            <div className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-white">50+</p>
              <p className="text-slate-300 mt-1">Partner Companies</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By Section - Rotating Marquee */}
      <section className="py-12 border-b border-slate-100 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-center text-slate-800 text-lg font-heading font-semibold mb-8">
            Our <span className="text-hfs-navy">Alumni</span> Works At
          </p>
          
          {/* Row 1 - Scrolling Left */}
          <div className="relative mb-3 py-4">
            <div className="flex animate-marquee-left items-center">
              {[...LOGOS_ROW_1, ...LOGOS_ROW_1].map((company, index) => (
                <div 
                  key={`row1-${index}`}
                  className="flex-shrink-0 mx-10 flex items-center justify-center"
                  style={{ width: '170px', height: '60px' }}
                >
                  <img 
                    src={company.logo} 
                    alt={company.name}
                    className="max-h-[48px] max-w-[145px] w-auto h-auto object-contain"
                    style={{ mixBlendMode: 'multiply' }}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {/* Row 2 - Scrolling Right */}
          <div className="relative py-4">
            <div className="flex animate-marquee-right items-center">
              {[...LOGOS_ROW_2, ...LOGOS_ROW_2].map((company, index) => (
                <div 
                  key={`row2-${index}`}
                  className="flex-shrink-0 mx-10 flex items-center justify-center"
                  style={{ width: '170px', height: '60px' }}
                >
                  <img 
                    src={company.logo} 
                    alt={company.name}
                    className="max-h-[48px] max-w-[145px] w-auto h-auto object-contain"
                    style={{ mixBlendMode: 'multiply' }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src={HFS_LOGO} 
                alt="HFS Logo" 
                className="h-20 w-20 mb-6"
              />
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-6">
                What is Hire For Skillz?
              </h2>
              <p className="text-slate-600 leading-relaxed mb-6">
                HFS is a premium B2B SaaS sales training academy. Our intensive program covers everything from consultative selling to enterprise deal closures.
              </p>
              <p className="text-slate-600 leading-relaxed mb-8">
                Over 28 months, we have successfully trained and placed 350+ candidates in top SaaS companies across India with a 96% placement rate. Our alumni are known for their ability to generate pipelines, close enterprise deals, and exceed targets consistently.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="bg-hfs-navy/10 p-2 rounded-lg">
                    <Briefcase className="h-5 w-5 text-hfs-navy" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Intensive Training</h4>
                    <p className="text-sm text-slate-500">Real-world SaaS sales scenarios</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="bg-hfs-navy/10 p-2 rounded-lg">
                    <Users className="h-5 w-5 text-hfs-navy" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900">Expert Mentorship</h4>
                    <p className="text-sm text-slate-500">Learn from industry leaders</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <h4 className="font-heading font-semibold text-hfs-navy text-lg mb-2">Market Research</h4>
                  <p className="text-sm text-slate-600">Understanding TAM, SAM, SOM and competitive landscapes</p>
                </div>
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <h4 className="font-heading font-semibold text-hfs-navy text-lg mb-2">Lead Generation</h4>
                  <p className="text-sm text-slate-600">Modern tools like Sales Navigator, Apollo, and Lusha</p>
                </div>
              </div>
              <div className="space-y-4 mt-8">
                <div className="bg-slate-50 p-6 rounded-xl border border-slate-100">
                  <h4 className="font-heading font-semibold text-hfs-navy text-lg mb-2">Discovery Calls</h4>
                  <p className="text-sm text-slate-600">BANT, MEDDIC, and consultative selling techniques</p>
                </div>
                <div className="bg-hfs-navy p-6 rounded-xl text-white">
                  <h4 className="font-heading font-semibold text-lg mb-2">Deal Closure</h4>
                  <p className="text-sm text-slate-200">Enterprise sales cycles and negotiation mastery</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Candidates Section */}
      <section id="candidates" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Interview-Ready Sales Talent
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Each candidate is trained on MEDDIC, BANT, and enterprise sales motions. View their skills, experience level, and sales strengths.
            </p>
            
            {!isAuthenticated && (
              <div className="mt-6 inline-flex items-center gap-2 bg-blue-50 text-blue-800 px-4 py-2 rounded-lg border border-blue-200">
                <Users className="h-4 w-4" />
                <span className="text-sm">Register with just your email to view full portfolios</span>
              </div>
            )}
          </div>

          {/* Search and Filters */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by name or skills..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(6); }}
                  className="pl-10 h-12 bg-white border-slate-200"
                  data-testid="search-candidates-input"
                />
              </div>
              
              {/* Role Filter */}
              <div className="flex gap-2 flex-wrap justify-center sm:justify-start">
                {[
                  { value: 'all', label: 'All Roles' },
                  { value: 'sdr', label: 'SDR/BDR' },
                  { value: 'ae', label: 'AE' },
                  { value: 'enterprise', label: 'Enterprise' },
                ].map(filter => (
                  <button
                    key={filter.value}
                    onClick={() => { setRoleFilter(filter.value); setVisibleCount(6); }}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      roleFilter === filter.value
                        ? 'bg-hfs-navy text-white'
                        : 'bg-white text-slate-600 border border-slate-200 hover:border-hfs-navy'
                    }`}
                    data-testid={`filter-${filter.value}`}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Results count */}
            <p className="text-sm text-slate-500 mt-4 text-center">
              Showing {displayedCandidates.length} of {mixedCandidates.length} candidates
              {roleFilter !== 'all' && ` (filtered by ${roleFilter.toUpperCase()})`}
            </p>
          </div>

          {/* Candidates Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-hfs-navy border-t-transparent rounded-full mx-auto" />
              <p className="mt-4 text-slate-500">Loading candidates...</p>
            </div>
          ) : mixedCandidates.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No candidates found. Try adjusting your filters.</p>
              {roleFilter !== 'all' && (
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setRoleFilter('all')}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 auto-rows-fr">
                {displayedCandidates.map((candidate, index) => (
                  <div 
                    key={candidate.id}
                    className="opacity-0 animate-fade-in h-full"
                    style={{ animationDelay: `${(index % 6) * 100}ms` }}
                  >
                    <CandidateCard
                      candidate={candidate}
                      isAuthenticated={isAuthenticated}
                      onViewPortfolio={handleViewPortfolio}
                      onRegisterClick={() => setRegisterOpen(true)}
                    />
                  </div>
                ))}
              </div>
              
              {/* Load More / View All */}
              {hasMore && (
                <div className="text-center mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={loadMore}
                    variant="outline"
                    className="border-2 border-hfs-navy text-hfs-navy hover:bg-hfs-navy/5 h-12 px-8"
                    data-testid="load-more-btn"
                  >
                    Load More
                  </Button>
                  <Button
                    onClick={showAll}
                    className="bg-hfs-navy hover:bg-hfs-navy-dark text-white h-12 px-8"
                    data-testid="view-all-btn"
                  >
                    View All Candidates
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              What Industry Leaders Say
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Trusted by top SaaS companies across India
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Testimonial 1 */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic text-sm">
                "Hired 2 SDRs in 10 days. Both hit quota in Q1. Pre-training on MEDDIC saved us months of onboarding."
              </p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-hfs-navy rounded-full flex items-center justify-center text-white font-semibold text-xs">
                  RK
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">Rajiv Kumar</p>
                  <p className="text-xs text-slate-500">VP Sales, Series C Logistics SaaS</p>
                </div>
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic text-sm">
                "Zero recruiter fees, fast process. Interviewed Monday, offered Wednesday. Candidate knew our tech stack already."
              </p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-hfs-navy rounded-full flex items-center justify-center text-white font-semibold text-xs">
                  PS
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">Priya Sharma</p>
                  <p className="text-xs text-slate-500">Head HR, Series B DevTools Company</p>
                </div>
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic text-sm">
                "Best hiring decision this year. AE closed ₹40L deal in first month. HFS candidates come ready to sell."
              </p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-hfs-navy rounded-full flex items-center justify-center text-white font-semibold text-xs">
                  AM
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">Amit Mehta</p>
                  <p className="text-xs text-slate-500">CRO, Series D HealthTech Enterprise</p>
                </div>
              </div>
            </div>

            {/* Testimonial 4 */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic text-sm">
                "Saved ₹2L+ in recruiter fees. Quality of candidates was exceptional. Will definitely hire again from HFS."
              </p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-hfs-navy rounded-full flex items-center justify-center text-white font-semibold text-xs">
                  SG
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">Sneha Gupta</p>
                  <p className="text-xs text-slate-500">Director, Series A B2B SaaS Startup</p>
                </div>
              </div>
            </div>

            {/* Testimonial 5 */}
            <div className="bg-slate-50 p-5 rounded-xl border border-slate-100">
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                    <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                  </svg>
                ))}
              </div>
              <p className="text-slate-700 mb-4 italic text-sm">
                "HFS trained candidates understand enterprise sales. Our BDR built ₹1.5Cr pipeline in 90 days."
              </p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-hfs-navy rounded-full flex items-center justify-center text-white font-semibold text-xs">
                  VR
                </div>
                <div>
                  <p className="font-semibold text-slate-900 text-sm">Vikram Rao</p>
                  <p className="text-xs text-slate-500">Sales Head, Enterprise SaaS Leader</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-hfs-navy">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Hire Without Recruiters. Interview This Week.
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Access our pool of interview-ready SDRs and AEs trained on real SaaS playbooks. No fees. No guesswork. Just talent that can close.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isAuthenticated ? (
              <Button 
                onClick={() => setRegisterOpen(true)}
                className="bg-hfs-gold hover:bg-hfs-gold-dark text-hfs-navy h-12 px-8 text-base font-semibold"
                data-testid="cta-register-btn"
              >
                Get Interview-Ready Talent Now
              </Button>
            ) : (
              <a href="#candidates">
                <Button 
                  className="bg-hfs-gold hover:bg-hfs-gold-dark text-hfs-navy h-12 px-8 text-base font-semibold"
                >
                  See Candidates Who Can Close
                </Button>
              </a>
            )}
          </div>
          <p className="text-slate-400 text-sm mt-4">Free to register • No recruiter fees • Interview in 48 hours</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <img src={HFS_LOGO} alt="HFS" className="h-10 w-10" />
              <div>
                <p className="font-heading font-semibold text-white">Hire For Skillz</p>
                <p className="text-sm">Transforming Sales Careers</p>
              </div>
            </div>
            <div className="flex gap-8 text-sm">
              <a href="https://hireforskillz.com" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                Main Website
              </a>
              <a href="#about" className="hover:text-white transition-colors">
                About Us
              </a>
              <a href="#candidates" className="hover:text-white transition-colors">
                Candidates
              </a>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p>&copy; {new Date().getFullYear()} Hire For Skillz. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
